import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { FileText, Download } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { useTechProfiles } from '@/hooks/use-crew';
import { useProjects } from '@/hooks/use-projects';
import { usePlatformModules } from '@/hooks/use-modules';
import { usePermissions } from '@/hooks/use-permissions';
import { useOrgCurrency } from '@/hooks/use-settings';
import { formatMoney } from '@/lib/currency';
import {
  useExpenseByTechReport,
  useExpenseByProjectReport,
  usePayrollSummaryReport,
  useCostCodeReport,
  usePayPeriods,
} from '@/hooks/use-payroll';

const PAYROLL_API = '/api/v1/payroll';

type ReportType = 'expense-by-tech' | 'expense-by-project' | 'payroll-summary' | 'cost-codes';

export const TECH_LIST_UNAVAILABLE_MESSAGE =
  'The technician list needs H3Crew access. Ask an Admin for a Crew role to run this report.';

function getExportUrl(reportType: ReportType, params: Record<string, string>, exportFormat: string) {
  const searchParams = new URLSearchParams({ ...params, export_format: exportFormat });
  return `${PAYROLL_API}/reports/${reportType}/?${searchParams.toString()}`;
}

export function ReportsPage() {
  const permissions = usePermissions();
  const canManagePayroll = permissions.hasMinModuleRole('payroll', 'manager');

  const [reportType, setReportType] = useState<ReportType>('payroll-summary');
  const [techId, setTechId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [payPeriodId, setPayPeriodId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [generated, setGenerated] = useState(false);

  const { isModuleEnabled } = usePlatformModules();
  const crewEnabled = isModuleEnabled('crew');
  const { data: techs, isError: techsUnavailable } = useTechProfiles(
    {},
    canManagePayroll && crewEnabled && reportType === 'expense-by-tech',
  );
  const canListTechs = crewEnabled && !techsUnavailable;
  const { data: projects } = useProjects({}, canManagePayroll && isModuleEnabled('projects'));
  const { data: payPeriods } = usePayPeriods({}, canManagePayroll);

  const techList = techs?.results ?? techs ?? [];
  const projectList = projects?.results ?? projects ?? [];
  const periodList = payPeriods ?? [];

  // Report queries (only enabled when generated and user is authorized)
  const reportEnabled = canManagePayroll && generated;
  const expenseByTech = useExpenseByTechReport({
    tech_id: reportEnabled && reportType === 'expense-by-tech' ? Number(techId) : 0,
    date_from: dateFrom,
    date_to: dateTo,
  });

  const expenseByProject = useExpenseByProjectReport({
    project_id: reportEnabled && reportType === 'expense-by-project' ? Number(projectId) : 0,
    date_from: dateFrom,
    date_to: dateTo,
  });

  const payrollSummary = usePayrollSummaryReport({
    pay_period_id: reportEnabled && reportType === 'payroll-summary' ? Number(payPeriodId) : 0,
  });

  const costCodeReport = useCostCodeReport(
    reportEnabled && reportType === 'cost-codes'
      ? { date_from: dateFrom, date_to: dateTo }
      : { date_from: '', date_to: '' }
  );

  const handleGenerate = () => {
    setGenerated(true);
  };

  const getExportParams = (): Record<string, string> => {
    const params: Record<string, string> = {};
    if (reportType === 'expense-by-tech') {
      params.tech_id = techId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
    } else if (reportType === 'expense-by-project') {
      params.project_id = projectId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
    } else if (reportType === 'payroll-summary') {
      params.pay_period_id = payPeriodId;
    } else {
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
    }
    return params;
  };

  const isLoading =
    expenseByTech.isLoading ||
    expenseByProject.isLoading ||
    payrollSummary.isLoading ||
    costCodeReport.isLoading;

  if (!canManagePayroll) {
    return <Navigate to="/payroll" replace />;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Reports</h1>
        <p className="text-gray-600 mt-1">Generate payroll and expense reports</p>
      </div>

      {/* Report Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Report Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label>Report Type</Label>
              <Select value={reportType} onValueChange={(v) => { setReportType(v as ReportType); setGenerated(false); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense-by-tech">Expense by Tech</SelectItem>
                  <SelectItem value="expense-by-project">Expense by Project</SelectItem>
                  <SelectItem value="payroll-summary">Payroll Summary</SelectItem>
                  <SelectItem value="cost-codes">Cost Code Spending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {reportType === 'expense-by-tech' && canListTechs && (
              <div>
                <Label htmlFor="report-tech">Technician</Label>
                <Select value={techId} onValueChange={setTechId}>
                  <SelectTrigger id="report-tech">
                    <SelectValue placeholder="Select tech" />
                  </SelectTrigger>
                  <SelectContent>
                    {(Array.isArray(techList) ? techList : []).map((t: { id: number; first_name: string; last_name: string }) => (
                      <SelectItem key={t.id} value={String(t.id)}>
                        {t.first_name} {t.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {reportType === 'expense-by-tech' && !canListTechs && (
              <p
                data-testid="tech-list-unavailable"
                className="text-sm text-gray-500 sm:col-span-1 lg:col-span-3 self-end"
              >
                {TECH_LIST_UNAVAILABLE_MESSAGE}
              </p>
            )}

            {reportType === 'expense-by-project' && (
              <div>
                <Label>Project</Label>
                <Select value={projectId} onValueChange={setProjectId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {(Array.isArray(projectList) ? projectList : []).map((p: { id: number; name: string }) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {reportType === 'payroll-summary' && (
              <div>
                <Label>Pay Period</Label>
                <Select value={payPeriodId} onValueChange={setPayPeriodId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    {(Array.isArray(periodList) ? periodList : []).map((p: { id: number; name: string }) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {reportType !== 'payroll-summary' && (
              <>
                <div>
                  <Label>Date From</Label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>
                <div>
                  <Label>Date To</Label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>
              </>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleGenerate}
              disabled={reportType === 'expense-by-tech' && !canListTechs}
            >
              <FileText className="h-4 w-4 mr-2" />
              Generate Report
            </Button>
            {generated && (
              <>
                <Button variant="outline" asChild>
                  <a href={getExportUrl(reportType, getExportParams(), 'csv')} download>
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </a>
                </Button>
                {reportType !== 'cost-codes' && (
                  <Button variant="outline" asChild>
                    <a href={getExportUrl(reportType, getExportParams(), 'pdf')} download>
                      <Download className="h-4 w-4 mr-2" />
                      Export PDF
                    </a>
                  </Button>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {generated && (
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">Generating report...</div>
            ) : reportType === 'expense-by-tech' && expenseByTech.data ? (
              <ExpenseByTechTable data={expenseByTech.data} />
            ) : reportType === 'expense-by-project' && expenseByProject.data ? (
              <ExpenseByProjectTable data={expenseByProject.data} />
            ) : reportType === 'payroll-summary' && payrollSummary.data ? (
              <PayrollSummaryTable data={payrollSummary.data} />
            ) : reportType === 'cost-codes' && costCodeReport.data ? (
              <CostCodeTable data={costCodeReport.data} />
            ) : (
              <div className="p-8 text-center text-gray-500">No data available</div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components for report tables
// ---------------------------------------------------------------------------

function ExpenseByTechTable({ data }: { data: import('@/lib/types').ExpenseByTechReport }) {
  const orgCurrency = useOrgCurrency();

  return (
    <div className="p-4 space-y-4">
      <div className="text-lg font-semibold">
        Expense Report: {data.tech_name}
        <span className="text-sm text-gray-500 ml-2">
          ({data.date_from} to {data.date_to})
        </span>
      </div>
      {data.categories.map((cat) => (
        <div key={cat.category}>
          <h3 className="font-medium text-gray-700 mb-2">
            {cat.category} — {formatMoney(cat.total, orgCurrency)}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="px-3 pb-2">Date</th>
                  <th className="px-3 pb-2">Description</th>
                  <th className="px-3 pb-2">Vendor</th>
                  <th className="px-3 pb-2 text-right">Amount</th>
                  <th className="px-3 pb-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {cat.items.map((item) => (
                  <tr key={item.id} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-1.5">{item.date}</td>
                    <td className="px-3 py-1.5">{item.description}</td>
                    <td className="px-3 py-1.5">{item.vendor}</td>
                    <td className="px-3 py-1.5 text-right">
                      {formatMoney(item.amount, orgCurrency)}
                    </td>
                    <td className="px-3 py-1.5">{item.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      <div className="border-t pt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
        <div>Expenses: <span className="font-semibold">{formatMoney(data.expense_total, orgCurrency)}</span></div>
        <div>Mileage: <span className="font-semibold">{formatMoney(data.mileage_total, orgCurrency)}</span></div>
        <div>Per Diem: <span className="font-semibold">{formatMoney(data.per_diem_total, orgCurrency)}</span></div>
        <div className="text-lg font-bold">Total: {formatMoney(data.grand_total, orgCurrency)}</div>
      </div>
    </div>
  );
}

function ExpenseByProjectTable({ data }: { data: import('@/lib/types').ExpenseByProjectReport }) {
  const orgCurrency = useOrgCurrency();

  return (
    <div className="p-4 space-y-4">
      <div className="text-lg font-semibold">
        Project Expense Report
        {data.date_from && (
          <span className="text-sm text-gray-500 ml-2">
            ({data.date_from} to {data.date_to})
          </span>
        )}
      </div>
      {data.categories.map((cat) => (
        <div key={cat.category}>
          <h3 className="font-medium text-gray-700 mb-2">
            {cat.category} — {formatMoney(cat.total, orgCurrency)}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="px-3 pb-2">Date</th>
                  <th className="px-3 pb-2">Tech</th>
                  <th className="px-3 pb-2">Description</th>
                  <th className="px-3 pb-2 text-right">Amount</th>
                  <th className="px-3 pb-2">Cost Code</th>
                </tr>
              </thead>
              <tbody>
                {cat.items.map((item) => (
                  <tr key={item.id} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-1.5">{item.date}</td>
                    <td className="px-3 py-1.5">{item.tech_name}</td>
                    <td className="px-3 py-1.5">{item.description}</td>
                    <td className="px-3 py-1.5 text-right">
                      {formatMoney(item.allocated_amount, orgCurrency)}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs">{item.cost_code}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {data.tech_breakdown.length > 0 && (
        <div className="border-t pt-3">
          <h3 className="font-medium mb-2">By Technician</h3>
          {data.tech_breakdown.map((tb) => (
            <div key={tb.tech_name} className="flex justify-between text-sm py-1">
              <span>{tb.tech_name}</span>
              <span className="font-medium">{formatMoney(tb.total, orgCurrency)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="border-t pt-3 text-lg font-bold">
        Grand Total: {formatMoney(data.grand_total, orgCurrency)}
      </div>
    </div>
  );
}

function PayrollSummaryTable({ data }: { data: import('@/lib/types').PayrollSummaryReport }) {
  const orgCurrency = useOrgCurrency();

  if (data.status === 'no_data') {
    return <div className="p-8 text-center text-gray-500">No payroll data for this period.</div>;
  }

  return (
    <div className="p-4 space-y-4">
      <div className="text-lg font-semibold">
        Payroll Summary: {data.pay_period_name}
        <span className="ml-2 text-sm text-gray-500">Status: {data.status}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="px-3 pb-2">Technician</th>
              <th className="px-3 pb-2 text-right">
                {data.techs.every((t) => t.regular_quantity_unit === data.techs[0]?.regular_quantity_unit)
                  ? `Reg ${data.techs[0]?.regular_quantity_unit === 'days' ? 'Days' : 'Hrs'}`
                  : 'Reg Qty'}
              </th>
              <th className="px-3 pb-2 text-right">OT Hrs</th>
              <th className="px-3 pb-2 text-right">Reg Pay</th>
              <th className="px-3 pb-2 text-right">OT Pay</th>
              <th className="px-3 pb-2 text-right">Per Diem</th>
              <th className="px-3 pb-2 text-right">Mileage</th>
              <th className="px-3 pb-2 text-right">Expenses</th>
              <th className="px-3 pb-2 text-right font-semibold">Total Reimb.</th>
              <th className="px-3 pb-2 text-right font-semibold">Gross Pay</th>
              <th className="px-3 pb-2 text-right font-semibold">Grand Total</th>
              <th className="px-3 pb-2">Adjustment</th>
            </tr>
          </thead>
          <tbody>
            {data.techs.map((tech) => {
              const gross = Number(tech.gross_pay);
              const reimb = Number(tech.total_reimbursements);
              const unitLabel = tech.regular_quantity_unit === 'days' ? 'days' : 'hrs';
              return (
                <tr key={tech.row_id} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-1.5 font-medium">{tech.tech_name}</td>
                  <td className="px-3 py-1.5 text-right">{tech.regular_quantity} {unitLabel}</td>
                  <td className="px-3 py-1.5 text-right">{tech.overtime_hours}</td>
                  <td className="px-3 py-1.5 text-right">{formatMoney(tech.regular_pay, orgCurrency)}</td>
                  <td className="px-3 py-1.5 text-right">{formatMoney(tech.overtime_pay, orgCurrency)}</td>
                  <td className="px-3 py-1.5 text-right">{formatMoney(tech.per_diem_total, orgCurrency)}</td>
                  <td className="px-3 py-1.5 text-right">{formatMoney(tech.mileage_total, orgCurrency)}</td>
                  <td className="px-3 py-1.5 text-right">{formatMoney(tech.expense_reimbursement_total, orgCurrency)}</td>
                  <td className="px-3 py-1.5 text-right">{formatMoney(tech.total_reimbursements, orgCurrency)}</td>
                  <td className="px-3 py-1.5 text-right font-semibold">{formatMoney(tech.gross_pay, orgCurrency)}</td>
                  <td className="px-3 py-1.5 text-right font-semibold">{formatMoney(gross + reimb, orgCurrency)}</td>
                  <td className="px-3 py-1.5 text-xs">{tech.adjustment_note ?? ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {data.totals && (
        <div className="border-t pt-3 text-sm grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div><span className="font-semibold">Gross Pay:</span> {formatMoney(data.totals.gross_pay, orgCurrency)}</div>
          <div><span className="font-semibold">Total Reimb.:</span> {formatMoney(data.totals.total_reimbursements, orgCurrency)}</div>
          <div className="font-bold text-lg">Grand Total: {formatMoney(data.totals.grand_total, orgCurrency)}</div>
        </div>
      )}
    </div>
  );
}

function CostCodeTable({ data }: { data: import('@/lib/types').CostCodeReport }) {
  const orgCurrency = useOrgCurrency();

  return (
    <div className="p-4 space-y-4">
      <div className="text-lg font-semibold">
        Cost Code Spending Report
        <span className="text-sm text-gray-500 ml-2">
          ({data.date_from} to {data.date_to})
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="px-3 pb-2">Code</th>
              <th className="px-3 pb-2">Name</th>
              <th className="px-3 pb-2">Category</th>
              <th className="px-3 pb-2 text-right">Expense Total</th>
              <th className="px-3 pb-2 text-right">Labor Hours</th>
              <th className="px-3 pb-2 text-right">Total Spend</th>
            </tr>
          </thead>
          <tbody>
            {data.cost_codes.map((cc) => (
              <tr key={cc.cost_code_id} className="border-b hover:bg-gray-50">
                <td className="px-3 py-1.5 font-mono font-medium">{cc.code}</td>
                <td className="px-3 py-1.5">{cc.name}</td>
                <td className="px-3 py-1.5 capitalize">{cc.category}</td>
                <td className="px-3 py-1.5 text-right">
                  {formatMoney(cc.expense_total, orgCurrency)}
                </td>
                <td className="px-3 py-1.5 text-right">{cc.labor_hours}</td>
                <td className="px-3 py-1.5 text-right font-medium">
                  {formatMoney(cc.total_spend, orgCurrency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border-t pt-3 text-lg font-bold">
        Grand Total: {formatMoney(data.grand_total, orgCurrency)}
      </div>
    </div>
  );
}
